import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Warehouse, MapPin } from 'lucide-react';

export default function WarehousesPage() {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    address: '',
    timezone: 'UTC'
  });

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    try {
      const response = await api.get('/warehouses');
      setWarehouses(response.data);
    } catch (error) {
      toast.error('Failed to fetch warehouses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingWarehouse) {
        await api.put(`/warehouses/${editingWarehouse.id}`, formData);
        toast.success('Warehouse updated');
      } else {
        await api.post('/warehouses', formData);
        toast.success('Warehouse created');
      }
      setDialogOpen(false);
      resetForm();
      fetchWarehouses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (warehouse) => {
    setEditingWarehouse(warehouse);
    setFormData({
      code: warehouse.code,
      name: warehouse.name,
      address: warehouse.address || '',
      timezone: warehouse.timezone || 'UTC'
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this warehouse?')) return;
    try {
      await api.delete(`/warehouses/${id}`);
      toast.success('Warehouse deleted');
      fetchWarehouses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const resetForm = () => {
    setEditingWarehouse(null);
    setFormData({
      code: '',
      name: '',
      address: '',
      timezone: 'UTC'
    });
  };

  return (
    <div className="space-y-6" data-testid="warehouses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">WAREHOUSES</h1>
          <p className="text-[#0A0A0A]/60 mt-1">Manage your warehouse locations</p>
        </div>
        <Button 
          className="bg-[#007AFF] hover:bg-[#0066D6]" 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          data-testid="add-warehouse-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Warehouse
        </Button>
      </div>

      {/* Warehouses Grid/Table */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
            </div>
          ) : warehouses.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Warehouse className="w-12 h-12 text-[#0A0A0A]/20 mb-4" />
              <p className="text-[#0A0A0A]/60">No warehouses found</p>
              <Button 
                className="mt-4 bg-[#007AFF] hover:bg-[#0066D6]"
                onClick={() => { resetForm(); setDialogOpen(true); }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Warehouse
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#E5E5EA]">
                  <TableHead className="text-overline text-[#0A0A0A]/50">Code</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Name</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Address</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Timezone</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Status</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {warehouses.map((warehouse) => (
                  <TableRow key={warehouse.id} className="border-b border-[#E5E5EA]" data-testid={`warehouse-row-${warehouse.code}`}>
                    <TableCell className="font-mono text-sm font-medium">{warehouse.code}</TableCell>
                    <TableCell className="font-medium">{warehouse.name}</TableCell>
                    <TableCell className="text-[#0A0A0A]/60 max-w-xs truncate">
                      {warehouse.address || '-'}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{warehouse.timezone}</TableCell>
                    <TableCell>
                      <Badge className={warehouse.is_active ? 'bg-[#34C759]/10 text-[#34C759]' : 'bg-[#0A0A0A]/10 text-[#0A0A0A]/60'}>
                        {warehouse.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(warehouse)} data-testid={`edit-warehouse-${warehouse.code}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(warehouse.id)} data-testid={`delete-warehouse-${warehouse.code}`}>
                          <Trash2 className="w-4 h-4 text-[#FF3B30]" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Warehouse Cards (Alternative View) */}
      {warehouses.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {warehouses.map((warehouse) => (
            <Card key={warehouse.id} className="border border-[#E5E5EA] shadow-none hover:border-[#007AFF] transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-[#007AFF]/10 rounded-sm flex items-center justify-center">
                    <Warehouse className="w-5 h-5 text-[#007AFF]" />
                  </div>
                  <Badge className={warehouse.is_active ? 'bg-[#34C759]/10 text-[#34C759]' : 'bg-[#0A0A0A]/10 text-[#0A0A0A]/60'}>
                    {warehouse.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <h3 className="text-heading text-lg text-[#0A0A0A] mb-1">{warehouse.name}</h3>
                <p className="text-mono text-sm text-[#0A0A0A]/50 mb-3">{warehouse.code}</p>
                {warehouse.address && (
                  <div className="flex items-start gap-2 text-sm text-[#0A0A0A]/60">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-2">{warehouse.address}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-heading text-xl">
              {editingWarehouse ? 'Edit Warehouse' : 'Add New Warehouse'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Warehouse Code *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="WH-001"
                required
                disabled={!!editingWarehouse}
                className="font-mono"
                data-testid="warehouse-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Main Warehouse"
                required
                data-testid="warehouse-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Warehouse St, City, State 12345"
                rows={2}
                data-testid="warehouse-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Input
                value={formData.timezone}
                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                placeholder="America/New_York"
                data-testid="warehouse-timezone-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#007AFF] hover:bg-[#0066D6]" data-testid="save-warehouse-btn">
                {editingWarehouse ? 'Update' : 'Create'} Warehouse
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
